import {SingletonService} from '@/common/dependency.configs'
import {inject, injectable} from 'inversify'
import ServiceUrlFactory from '@/common/services/Service/ServiceUrlFactory/ServiceUrlFactory'
import DEPENDENCYTYPES from '../dependency.types'
import {useLDFlag} from 'launchdarkly-vue-client-sdk'
import type IWorkspaceMetadataStore from './Workspace/IWorkspaceMetadataStore'
import WindowFacade from '@/common/facade/WindowFacade'

@SingletonService
@injectable()
class ConfigService {
  constructor(
    @inject(ServiceUrlFactory)
    private serviceUrlFactory: ServiceUrlFactory,
    @inject(DEPENDENCYTYPES.IWorkspaceMetadataStore)
    private store: IWorkspaceMetadataStore,
    @inject(WindowFacade)
    private windowFacade: WindowFacade,
  ) {}

  public async bootstrap() {
    const useOriginBasedServiceUrls = useLDFlag('openanywhere-use-origin-for-oa-services', false).value || window.MULTITENANCY_ENABLED
    if (useOriginBasedServiceUrls) {
      await this.bootstrapServiceUrlsFromOrigin()
    } else {
      await this.bootstrapServiceUrlsFromLaunchpad()
    }
  }

  public async bootstrapServiceUrlsFromLaunchpad() {
    this.store.serviceUrls = await this.serviceUrlFactory.getAll()
    this.store.serviceUrls['none'] = '/v1'
    this.store.serviceUrls['launchpad'] = '/v1/launchpadREST'
  }

  private createFullyQualifiedUrl(baseApi: string): string {

    const location = this.windowFacade.getLocation()

    const protocolAndHostname = location.protocol + '//' + location.host

    // This ensures that the baseApi will work when it has a leading slash or not.
    const join = baseApi[0] === '/' ? '' : '/'

    return protocolAndHostname + join + baseApi
  }

  public async bootstrapServiceUrlsFromOrigin() {

    this.store.serviceUrls['none'] = this.createFullyQualifiedUrl('/v1')
    this.store.serviceUrls['launchpad'] = this.createFullyQualifiedUrl('/v1/launchpadREST')

    const serviceUrlDetails = await this.serviceUrlFactory.getAllDetails()

    const services = serviceUrlDetails.services

    if (!services) {
      console.error('Unexpectedly received null services from launchpad\'s active service endpoint, received: ', serviceUrlDetails)
      return
    }

    if (services.length === 0) {
      console.error('Received zero services from launchpad\'s active service endpoint, user will not be able to make any calls to the backend.')
      return
    }

    for (const service of services) {

      if (!service.serviceCode || service.serviceCode.trim() === '') {
        console.error('Received launchpad active service that contains no valid service code, user will not be able to make calls to this service: ', service)
        continue
      }

      if (!service.baseApi || service.baseApi.trim() === '') {
        console.error(`Received launchpad active service ${service.serviceCode} that contains no valid path in the url, user will not be able to make calls to this service: `, service)
        continue
      }

      this.store.serviceUrls[service.serviceCode] = this.createFullyQualifiedUrl(service.baseApi)

    }

  }

}

export default ConfigService
